/**
 * "Delete everything you have about me." The article 17 tool, on screen.
 *
 * TWO STAGES, AND THE SECOND ONE IS THE POINT. A form that answers "done" hides
 * the two things this operation cannot do, and both are things the operator has
 * to know within the month the law gives them to reply: a thread the person
 * REPLIED to is left alone, and the business-register mirror is a copy of a
 * public register that the next sync would restore. Reporting them is not an
 * excuse - it is the difference between a request that was answered and a
 * request that was half answered by a machine that said nothing.
 *
 * ⚠ IT ADDS THEM TO THE DO-NOT-CONTACT LIST AND THAT ROW IS PERMANENT. Erasing
 * the last trace of somebody sounds like the complete answer and is the wrong
 * one: with nothing left saying they objected, the next audience built off the
 * register enrols them again. The form says so before the button, not after.
 */
import { useState } from "react";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { eraseAddress, type EraseReport } from "../../../../lib/marketingRetention";
import { Btn, Modal } from "../../../ui";
import { Note, num } from "../shared";

export function EraseAddressModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const t = useT();
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [report, setReport] = useState<EraseReport | null>(null);

  const ready = email.trim().length > 2 && reason.trim().length > 0;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setFailed(false);
    try {
      const res = await eraseAddress({ email: email.trim(), reason: reason.trim() });
      setReport(res.report);
      // The list behind the dialog now has a row it did not have. Refreshed
      // here rather than on close, so it is already right when the report is
      // read against it.
      onDone();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  if (report) {
    return (
      <Modal
        title={t("marketing.erase.doneTitle")}
        label={t("marketing.erase.doneTitle")}
        onClose={onClose}
        className="max-w-[460px]"
      >
        <p className="crm-sub mt-1">{t("marketing.erase.doneIntro", { email: report.emailKey })}</p>

        <ul className="crm-sub mt-3 space-y-1.5">
          <li>{t("marketing.erase.didRecipients", { n: num(report.recipients) })}</li>
          <li>{t("marketing.erase.didThreads", { n: num(report.threads) })}</li>
          <li>{t("marketing.erase.didContacts", { n: num(report.contacts) })}</li>
          <li>
            {t(report.wasSuppressed ? "marketing.erase.didSuppressAgain" : "marketing.erase.didSuppress")}
          </li>
        </ul>

        {/* The two things it could not do. Drawn as a warning rather than as a
            fourth bullet, because they are work that is still outstanding. */}
        {report.threadsWithReply > 0 || report.registerRows > 0 ? (
          <Note tone="warn" icon={<AlertTriangle className="size-3.5" />} className="mt-3.5">
            {report.threadsWithReply > 0 ? (
              <span className="block">
                {t("marketing.erase.leftThreads", { n: num(report.threadsWithReply) })}
              </span>
            ) : null}
            {report.registerRows > 0 ? (
              <span className="mt-1 block">
                {t("marketing.erase.leftRegister", { n: num(report.registerRows) })}
              </span>
            ) : null}
          </Note>
        ) : null}

        <div className="crm-m-foot">
          <Btn type="button" tone="brand" onClick={onClose}>
            {t("common.close")}
          </Btn>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={t("marketing.erase.title")}
      label={t("marketing.erase.title")}
      onClose={onClose}
      className="max-w-[460px]"
    >
      <form onSubmit={submit} className="mt-1 flex flex-col gap-3">
        <Note tone="warn" icon={<AlertTriangle className="size-3.5" />}>
          {t("marketing.erase.warning")}
        </Note>

        <div>
          <span className="crm-lab">{t("marketing.suppression.col.value")} *</span>
          <label className="crm-inp">
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("marketing.suppression.emailPlaceholder")}
              autoComplete="off"
              spellCheck={false}
              autoFocus
              required
            />
          </label>
        </div>

        <div>
          {/* Required, and stored on the row it writes. The case this exists
              for is a request from a named person on a dated day, and that is
              the fact somebody asks about months later. */}
          <span className="crm-lab">{t("marketing.erase.reason")} *</span>
          <label className="crm-inp">
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("marketing.erase.reasonPlaceholder")}
              autoComplete="off"
              required
            />
          </label>
        </div>

        {failed ? <Note tone="bad">{t("marketing.erase.failed")}</Note> : null}

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="submit" tone="danger" disabled={busy || !ready}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
            {t("marketing.erase.confirm")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
